import React, { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import TransactionResultModal from '../TransactionResultModal/TransactionResultModal';
import { CPAY_SUCCESS_STATUS } from '../../utils/constants';
import { routes } from '../../utils/routerHelper';
import { useTranslation } from 'react-i18next';
import { QueryFunctionContext, useQuery } from 'react-query';
import { Spin } from 'antd';
import {
  backToCeClicked,
  creditAddedScreenOpen,
  technicalErrorCancelClicked,
  technicalErrorPageOpen
} from '../../utils/analytika/events';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { ANALYTIKA_EVENTS_V2, ANALYTIKA_SCREENS } from '../../utils/analytika/enums';

type CheckoutBackRedirectProps = {
  fetchUserWalletInvoice: (id: string) => { currency: string; amount: number; status: string };
};

const CheckoutBackRedirect = ({ fetchUserWalletInvoice }: CheckoutBackRedirectProps) => {
  const { search } = useLocation();
  const history = useHistory();
  const { t } = useTranslation();

  const urlParams = useMemo(() => new URLSearchParams(search), [search]);
  const invoiceId = urlParams.get('invoiceId') || '';
  const isSuccessFromRedirect = urlParams.get('status') === CPAY_SUCCESS_STATUS;

  const fetchUserWalletQuery = ({ queryKey }: QueryFunctionContext<string[]>) => {
    const [, invoiceId] = queryKey;

    return fetchUserWalletInvoice(invoiceId);
  };

  const {
    data,
    isSuccess: isInvoiceFetchSuccess,
    isLoading
  } = useQuery(['invoiceId', invoiceId], fetchUserWalletQuery, {
    enabled: Boolean(isSuccessFromRedirect && invoiceId)
  });

  const { amount, currency } = data || { amount: null, currency: null, status: null };

  const amountTitle = `${amount} ${currency} ${t('modal.transactionResult.amountAddedTitle')}`;

  const isSuccess = isSuccessFromRedirect && isInvoiceFetchSuccess;

  useEffect(() => {
    if (!isLoading) {
      isSuccess
        ? publishAV2Event(
            {
              event_name: ANALYTIKA_EVENTS_V2.EXPR_ACKNOWLEDGE_CREDIT_ADDED,
              event_version: 1
            },
            ANALYTIKA_SCREENS.CREDIT_ADDED
          )
        : publishAV2Event(
            {
              event_name: ANALYTIKA_EVENTS_V2.EXPR_ACKNOWLEDGE_PAGE_OPEN_ERROR,
              event_version: 1
            },
            ANALYTIKA_SCREENS.TECHNICAL_ERROR
          );
    }
  }, [isLoading]);

  return isLoading ? (
    <Spin data-testid="checkoutBackRedirectSpin" />
  ) : (
    <TransactionResultModal
      isSuccess={isSuccess}
      visible
      onClose={() => {
        history.push(routes.profile);
      }}
      notificationTitle={isSuccess ? amountTitle : t('modal.transactionResult.technicalError')}
      notificationText={isSuccess ? t('modal.transactionResult.amountAddedText') : ''}
      primaryButtonText={isSuccess ? t('modal.transactionResult.goBackToExpress') : ''}
      secondaryButtonText={isSuccess ? '' : t('modal.transactionResult.cancel')}
      onProceed={() => {
        history.push(routes.deliveriesDetailed);
      }}
      analytikaProceedEvent={backToCeClicked({ amount, currency })}
      analytikaCancelEvent={technicalErrorCancelClicked({ wallet_balance: amount, currency })}
      analytikaOpenEvent={
        isSuccess ? creditAddedScreenOpen({ amount, currency }) : technicalErrorPageOpen({ invoiceId })
      }
    />
  );
};

const mapDispatchToProps = ({ invoices: { fetchUserWalletInvoice } }: any) => ({
  fetchUserWalletInvoice
});

export default connect(null, mapDispatchToProps)(CheckoutBackRedirect);
